import React from 'react';
import PropTypes from 'prop-types';
import { Link, Icon } from '@resy/ui-kitchen';
import getCurrentMomentYear from '../AmexGDAB2BDetails';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import { handleMktAnalytics } from '../../AmexMarketingUtils/AmexUtils';
import { CTA_EVENT_NAME } from '../../AmexMarketingUtils/constants';
import './AmexGDAB2BFooter.scss';

const AmexGDAB2BFooter = (
  {
    baseRequestUrl,
    footerConfig: {
      copyright,
      logo,
      logo_aria: logoAria,
      logo_headline: logoHeadline,
      logo_apple: logoApple,
      logo_fb: logoFacebook,
      logo_instagram: logoInstagram,
      logo_twitter: logoTwitter,
      links,
    },
    Analytics,
    matchMedia,
  }
) => {
  const primaryLinks = links?.primary_links;
  const secondaryLinks = links?.secondary_links;
  const appLinks = links?.app_links;

  const renderCopyright = () => {
    const copyWithCurrYear = copyright.replace('{currentYear}', getCurrentMomentYear());
    const sanitizedCopyWithCurrYear = sanitizeString(copyWithCurrYear);
    return (
      <p
        className="AmexGDAB2BFooter__copyright"
        dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
          __html: sanitizedCopyWithCurrYear,
        }}
      />
    );
  };

  const handleClick = (label) => {
    const extraParam = {
      action_name: 'Footer Link - Click',
      item_name: label,
    };
    handleMktAnalytics(Analytics.event, CTA_EVENT_NAME, extraParam);
  };

  const displayLinks = (link) => {
    const { label, url, name } = link;

    switch (name) {
      case 'Facebook':
        return (
          <li key={label} className="AmexGDAB2BFooter__app-link">
            <Link
              href={url}
              aria-label={label}
              target="_blank"
              onClick={() => handleClick(label)}
            >
              <span aria-hidden="true">
                <Icon url={`${baseRequestUrl}AmexGDAB2B/logos/${logoFacebook}`} />
              </span>
            </Link>
          </li>
        );
      case 'Instagram':
        return (
          <li key={label} className="AmexGDAB2BFooter__app-link">
            <Link
              href={url}
              aria-label={label}
              target="_blank"
              onClick={() => handleClick(label)}
            >
              <span aria-hidden="true">
                <Icon url={`${baseRequestUrl}AmexGDAB2B/logos/${logoInstagram}`} />
              </span>
            </Link>
          </li>
        );
      case 'Twitter':
        return (
          <li key={label} className="AmexGDAB2BFooter__app-link">
            <Link
              href={url}
              target="_blank"
              onClick={() => handleClick(label)}
            >
              <img src={`${baseRequestUrl}AmexGDAB2B/logos/${logoTwitter}`} alt={label} />
            </Link>
          </li>
        );
      case 'iOS':
        return (
          <li key={label} className="AmexGDAB2BFooter__app-link ios">
            <Link
              href={url}
              target="_blank"
              onClick={() => handleClick(label)}
            >
              <span aria-hidden="true">
                <Icon url={`${baseRequestUrl}AmexGDAB2B/logos/${logoApple}`} />
              </span>
              <span>{label}</span>
            </Link>
          </li>
        );
      default:
        if (!label || !url) return null;
        return (
          <li key={label} className="AmexGDAB2BFooter__link">
            <Link
              href={url}
              target="_blank"
              onClick={() => handleClick(label)}
            >
              {label}
            </Link>
          </li>
        );
    }
  };

  const renderSecondaryNav = () => (
    <ul className="AmexGDAB2BFooter__secondary-nav">
      {secondaryLinks?.map((link) => displayLinks(link))}
    </ul>
  );

  const renderAppNav = () => (
    <ul className="AmexGDAB2BFooter__apps-nav">
      {appLinks?.map((link) => displayLinks(link))}
    </ul>
  );

  return (
    <footer className="container" aria-label="GDAB2B footer">
      <div className="AmexGDAB2BFooter">
        <h2 className="AmexGDAB2BFooter__logo">
          <span className="AmexGDAB2BFooter__logo--headline">{logoHeadline}</span>
          <span>
            <Icon
              url={`${baseRequestUrl}AmexGDAB2B/logos/${logo}`}
            />
            <span className="sr-only">{logoAria}</span>
          </span>
        </h2>
        <ul className="AmexGDAB2BFooter__primary-nav">
          {primaryLinks?.map((link) => displayLinks(link))}
        </ul>
        <hr role="presentation" />
        {matchMedia.md ? (
          <>
            {renderAppNav()}
            {renderSecondaryNav()}
          </>
        ) : (
          <>
            {renderSecondaryNav()}
            {renderAppNav()}
          </>
        )}
        {renderCopyright()}
      </div>
    </footer>
  );
};

AmexGDAB2BFooter.propTypes = {
  baseRequestUrl: PropTypes.string.isRequired,
  footerConfig: PropTypes.shape({
    copyright: PropTypes.string.isRequired,
    logo: PropTypes.string.isRequired,
    logo_aria: PropTypes.string.isRequired,
    logo_headline: PropTypes.string.isRequired,
    logo_apple: PropTypes.string.isRequired,
    logo_fb: PropTypes.string.isRequired,
    logo_instagram: PropTypes.string.isRequired,
    logo_twitter: PropTypes.string.isRequired,
    links: PropTypes.shape({
      primary_links: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      secondary_links: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      app_links: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
      event_tag: PropTypes.string.isRequired,
    }),
  }).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    ipadLandscape: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
};

export default AmexGDAB2BFooter;
